import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ShieldCheck, X } from "lucide-react";
import { Button } from "@/components/ui/button";

const STORAGE_KEY = "egs_consent_v1";

const ConsentBanner = () => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    try {
      if (!localStorage.getItem(STORAGE_KEY)) {
        const t = setTimeout(() => setVisible(true), 400);
        return () => clearTimeout(t);
      }
    } catch {
      setVisible(true);
    }
  }, []);

  const dismiss = (value: "accepted" | "dismissed") => {
    try {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({ value, at: new Date().toISOString() }),
      );
    } catch {
      // ignore
    }
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      role="dialog"
      aria-modal="false"
      aria-labelledby="consent-title"
      aria-describedby="consent-desc"
      className="pointer-events-none fixed inset-x-0 bottom-0 z-[60] flex justify-center p-4 sm:inset-x-auto sm:right-6 sm:bottom-6 sm:justify-end sm:p-0"
    >
      <div
        className="pointer-events-auto relative w-full max-w-md animate-fade-in-up rounded-2xl border border-secondary/40 bg-[hsl(218_60%_14%)] text-[hsl(0_0%_100%)] shadow-2xl ring-1 ring-secondary/20"
        style={{ boxShadow: "0 20px 60px -20px hsl(220 25% 4% / 0.55), 0 0 0 1px hsl(42 62% 54% / 0.25)" }}
      >
        <div className="absolute inset-x-0 top-0 h-[2px] rounded-t-2xl bg-gradient-to-r from-transparent via-secondary to-transparent" />
        <button
          type="button"
          onClick={() => dismiss("dismissed")}
          aria-label="Dismiss privacy notice"
          className="absolute right-3 top-3 inline-flex h-8 w-8 items-center justify-center rounded-full text-white/60 transition-colors hover:bg-white/10 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-secondary"
        >
          <X className="h-4 w-4" />
        </button>

        <div className="flex gap-3 p-4 sm:gap-4 sm:p-6">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-secondary/15 ring-1 ring-secondary/40">
            <ShieldCheck className="h-5 w-5 text-secondary" aria-hidden="true" />
          </div>
          <div className="min-w-0 flex-1">
            <h2
              id="consent-title"
              className="font-display text-base font-semibold tracking-tight text-white"
            >
              Privacy &amp; Data Protection
            </h2>
            <p
              id="consent-desc"
              className="mt-2 line-clamp-3 text-sm leading-relaxed text-white/75 sm:line-clamp-none"
            >
              We use essential cookies and first-party analytics to improve
              website performance, understand visitor behavior, and enhance
              your experience. We never sell your personal information and do
              not use third-party advertising trackers.
            </p>
            <p className="mt-3 hidden rounded-lg border border-secondary/25 bg-white/[0.04] p-3 text-xs leading-relaxed text-white/70 sm:block">
              Your information is protected using industry-standard security
              practices and is used only for legitimate business purposes,
              including responding to inquiries, scheduling appointments, and
              improving our services.
            </p>

            <div className="mt-5 flex flex-col-reverse gap-2 sm:flex-row sm:items-center sm:justify-end">
              <Button
                asChild
                variant="ghost"
                size="sm"
                className="text-white/80 hover:bg-white/10 hover:text-white"
              >
                <Link to="/privacy" onClick={() => dismiss("dismissed")}>
                  Privacy Policy
                </Link>
              </Button>
              <Button
                size="sm"
                onClick={() => dismiss("accepted")}
                className="bg-secondary text-[hsl(220_30%_10%)] hover:bg-secondary/90 focus-visible:ring-secondary"
              >
                Accept
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConsentBanner;
